import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import masterService from '../../services/masterService';
import MasterPageWrapper from './common/MasterPageWrapper';
import ConfirmModal from './components/ConfirmModal';
import SampleTypeMultiSelect from './components/SampleTypeMultiSelect';
import { selectionFromDetail, selectionToPayload } from '../../utils/sampleTypeSelection';
import CommonPagination from '../../components/common/CommonPagination';
import { PAGINATION_CONFIG } from '../../config/paginationConfig';
import './InvestigationMasterPage.css';
import {
  DISCIPLINE,
  isRadiologyDepartment,
  isRadiologyInvestigation,
  PATHOLOGY_REPORT_TYPES,
  RADIOLOGY_REPORT_TYPES
} from '../../utils/disciplineHelper';

const GENDER_OPTIONS = [
  { value: 'B', label: 'Both (Universal)' },
  { value: 'M', label: 'Male Only' },
  { value: 'F', label: 'Female Only' }
];

const INITIAL_INVESTIGATION = {
  investigationId: 0,
  testCode: '',
  name: '',
  shortName: '',
  subCategoryId: '',
  invType: 'Investigation',
  reportType: 1,
  genderInvestigate: 'B',
  fromAge: 0,
  toAge: 36500,
  sampleTypeId: null,
  sampleTypeIds: [],
  containerId: null,
  sampleQty: '2 ml',
  logisticTemp: 'Room Temp',
  timeLimit: '24 Hours',
  tatIntimation: 30,
  baseRate: 0.00,
  rate: 0.00,
  maxDiscount: 0.00,
  isNabl: false,
  reporting: true,
  booking: true,
  printSeparate: false,
  isUrgent: false,
  isActive: true
};

export const InvestigationMasterPage = ({ showToast }) => {
  const navigate = useNavigate();

  // View Mode: 'form' | 'list'
  const [viewMode, setViewMode] = useState('form');
  const [isEditMode, setIsEditMode] = useState(false);

  // Diagnostic Discipline: 'PATHOLOGY' | 'RADIOLOGY'
  const [formDiscipline, setFormDiscipline] = useState(DISCIPLINE.PATHOLOGY);
  const [disciplineFilter, setDisciplineFilter] = useState('all'); // 'all' | 'pathology' | 'radiology'

  // Form State
  const [formData, setFormData] = useState(INITIAL_INVESTIGATION);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Lookups
  const [departments, setDepartments] = useState([]);
  const [sampleTypes, setSampleTypes] = useState([]);
  const [containers, setContainers] = useState([]);

  // List State
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [nablFilter, setNablFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGINATION_CONFIG.defaultPageSize);

  // Reset to page 1 on filter/search change
  useEffect(() => {
    setPage(1);
  }, [searchQuery, deptFilter, nablFilter, statusFilter, disciplineFilter]);

  // Confirm Modal
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);

  // Filtered departments by discipline
  const pathologyDepartments = useMemo(() => departments.filter(d => !isRadiologyDepartment(d)), [departments]);
  const radiologyDepartments = useMemo(() => departments.filter(d => isRadiologyDepartment(d)), [departments]);
  const availableFormDepartments = useMemo(() => {
    return formDiscipline === DISCIPLINE.RADIOLOGY ? radiologyDepartments : pathologyDepartments;
  }, [formDiscipline, radiologyDepartments, pathologyDepartments]);

  // Load Lookups
  const loadLookups = useCallback(async () => {
    try {
      const [deptRes, stRes, scRes] = await Promise.all([
        masterService.getDepartments({ isActive: true }),
        masterService.getSampleTypes(),
        masterService.getSampleContainers()
      ]);
      if (deptRes.success && deptRes.data) setDepartments(deptRes.data);
      if (stRes.success && stRes.data) setSampleTypes(stRes.data);
      if (scRes.success && scRes.data) setContainers(scRes.data);
    } catch (err) {
      console.error('Failed to load lookups', err);
    }
  }, []);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  // Load Investigations for List
  const fetchInvestigations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await masterService.getInvestigations({
        page,
        pageSize,
        search: searchQuery.trim() || undefined,
        subCategoryId: deptFilter ? parseInt(deptFilter, 10) : undefined,
        isNabl: nablFilter === 'all' ? undefined : nablFilter === 'nabl',
        isActive: statusFilter === 'all' ? undefined : statusFilter === 'active',
        discipline: disciplineFilter !== 'all' ? disciplineFilter.toUpperCase() : undefined
      });
      if (res.success && res.data) {
        setItems(res.data.items || []);
        setTotalCount(res.data.totalCount || 0);
      }
    } catch (err) {
      showToast?.('Failed to load investigations catalog.', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchQuery, deptFilter, nablFilter, statusFilter, disciplineFilter, showToast]);

  useEffect(() => {
    if (viewMode === 'list') {
      fetchInvestigations();
    }
  }, [viewMode, fetchInvestigations]);

  // Handle Form Change
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Handle Discipline Switch
  const handleDisciplineChange = (newDiscipline) => {
    setFormDiscipline(newDiscipline);
    const targetDepts = newDiscipline === DISCIPLINE.RADIOLOGY ? radiologyDepartments : pathologyDepartments;
    const isCurrentValid = targetDepts.some(d => d.subCategoryId === Number(formData.subCategoryId));
    const nextDeptId = isCurrentValid ? formData.subCategoryId : (targetDepts[0]?.subCategoryId || '');

    setFormData(prev => ({
      ...prev,
      subCategoryId: nextDeptId,
      reportType: newDiscipline === DISCIPLINE.RADIOLOGY ? 5 : (prev.reportType === 5 ? 1 : prev.reportType),
      sampleQty: newDiscipline === DISCIPLINE.RADIOLOGY ? 'Scan / Study' : (prev.sampleQty === 'Scan / Study' ? '2 ml' : prev.sampleQty)
    }));
  };

  // Add New Form
  const handleAddNew = (disciplineOverride) => {
    const randomCode = `INV${Math.floor(1000 + Math.random() * 9000)}`;
    const disc = disciplineOverride || (disciplineFilter !== 'all' ? disciplineFilter.toUpperCase() : DISCIPLINE.PATHOLOGY);
    setFormDiscipline(disc);
    const targetDepts = disc === DISCIPLINE.RADIOLOGY ? radiologyDepartments : pathologyDepartments;

    setFormData({
      ...INITIAL_INVESTIGATION,
      testCode: randomCode,
      subCategoryId: targetDepts[0]?.subCategoryId || departments[0]?.subCategoryId || '',
      reportType: disc === DISCIPLINE.RADIOLOGY ? 5 : 1,
      sampleQty: disc === DISCIPLINE.RADIOLOGY ? 'Scan / Study' : '2 ml'
    });
    setErrors({});
    setIsEditMode(false);
    setViewMode('form');
  };

  // Edit Row
  const handleEditRow = useCallback(async (row) => {
    try {
      const isRad = isRadiologyInvestigation(row);
      setFormDiscipline(isRad ? DISCIPLINE.RADIOLOGY : DISCIPLINE.PATHOLOGY);
      const res = await masterService.getInvestigationById(row.investigationId);
      if (res.success && res.data) {
        const d = res.data;
        const testIsRad = d.reportType === 5 || isRadiologyDepartment({ subCategoryId: d.subCategoryId, name: d.departmentName });
        setFormDiscipline(testIsRad ? DISCIPLINE.RADIOLOGY : DISCIPLINE.PATHOLOGY);
        setFormData({
          investigationId: d.investigationId,
          testCode: d.testCode || '',
          name: d.name || '',
          shortName: d.shortName || d.name || '',
          subCategoryId: d.subCategoryId || '',
          invType: d.invType || 'Investigation',
          reportType: d.reportType || (testIsRad ? 5 : 1),
          genderInvestigate: d.genderInvestigate || 'B',
          fromAge: d.fromAge ?? 0,
          toAge: d.toAge ?? 36500,
          sampleTypeId: selectionFromDetail(d).defaultId,
          sampleTypeIds: selectionFromDetail(d).ids,
          containerId: d.containerId || null,
          sampleQty: d.sampleQty || (testIsRad ? 'Scan / Study' : '2 ml'),
          logisticTemp: d.logisticTemp || 'Room Temp',
          timeLimit: d.timeLimit || '24 Hours',
          tatIntimation: d.tatIntimation || 30,
          baseRate: d.baseRate || 0.00,
          rate: d.rate || d.baseRate || 0.00,
          maxDiscount: d.maxDiscount || 0.00,
          isNabl: !!d.isNabl,
          reporting: d.reporting !== false,
          booking: d.booking !== false,
          printSeparate: !!d.printSeparate,
          isUrgent: !!d.isUrgent,
          isActive: d.isActive !== false
        });
      } else {
        const testIsRad = row.reportType === 5 || isRadiologyDepartment({ subCategoryId: row.subCategoryId, name: row.departmentName });
        setFormDiscipline(testIsRad ? DISCIPLINE.RADIOLOGY : DISCIPLINE.PATHOLOGY);
        setFormData({
          ...INITIAL_INVESTIGATION,
          investigationId: row.investigationId,
          testCode: row.testCode || '',
          name: row.name || '',
          subCategoryId: row.subCategoryId || '',
          reportType: row.reportType || (testIsRad ? 5 : 1),
          baseRate: row.baseRate || 0.00,
          rate: row.rate || 0.00,
          isNabl: !!row.isNabl,
          isActive: row.isActive !== false
        });
      }
      setErrors({});
      setIsEditMode(true);
      setViewMode('form');
    } catch (err) {
      showToast?.('Failed to load investigation details.', 'error');
    }
  }, [showToast]);

  // Validation
  const validate = () => {
    const errs = {};
    if (!formData.name.trim()) errs.name = 'Test Name is required.';
    if (!formData.testCode.trim()) errs.testCode = 'Test Code is required.';
    if (!formData.subCategoryId) errs.subCategoryId = 'Department is required.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Submit Form
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      const payload = {
        ...formData,
        testCode: formData.testCode.trim().toUpperCase(),
        name: formData.name.trim(),
        shortName: formData.shortName.trim() || formData.name.trim(),
        subCategoryId: parseInt(formData.subCategoryId, 10),
        ...selectionToPayload(formData.sampleTypeIds || [], formData.sampleTypeId),
        rate: parseFloat(formData.rate) || 0,
        baseRate: parseFloat(formData.baseRate) || parseFloat(formData.rate) || 0
      };

      let res;
      if (isEditMode) {
        res = await masterService.updateInvestigation(formData.investigationId, payload);
      } else {
        res = await masterService.createInvestigation(payload);
      }

      if (res.success) {
        showToast?.(`Investigation ${isEditMode ? 'updated' : 'created'} successfully!`, 'success');
        setViewMode('list');
        fetchInvestigations();
      } else {
        showToast?.(res.message || 'Operation failed.', 'error');
      }
    } catch (err) {
      showToast?.('Error saving investigation.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Status Toggle
  const handleConfirmStatus = async () => {
    if (!confirmTarget) return;
    setStatusUpdating(true);
    try {
      const newStatus = !confirmTarget.isActive;
      const res = await masterService.updateInvestigationStatus(confirmTarget.investigationId, newStatus);
      if (res.success) {
        showToast?.(`Investigation ${newStatus ? 'activated' : 'deactivated'}.`, 'success');
        setItems(prev => prev.map(x => x.investigationId === confirmTarget.investigationId ? { ...x, isActive: newStatus } : x));
      } else {
        showToast?.(res.message || 'Status update failed.', 'error');
      }
    } catch (err) {
      showToast?.('Error updating status.', 'error');
    } finally {
      setStatusUpdating(false);
      setConfirmTarget(null);
    }
  };

  return (
    <MasterPageWrapper
      masterTitle={viewMode === 'form' ? (isEditMode ? 'Update Investigation' : 'Create Investigation') : 'Investigation Master'}
      masterName="Investigation"
      viewMode={viewMode}
      className={viewMode === 'list' ? 'inv-master-container' : ''}
      onToggleView={(mode) => {
        if (mode === 'form' && !isEditMode) handleAddNew();
        else setViewMode(mode);
      }}
    >
      {/* ─── FULL-SCREEN FORM VIEW ─── */}
      {viewMode === 'form' && (
        <div className="glass-card fade-in">
          <form onSubmit={handleSubmit} noValidate className="inv-form-compact">
            {/* Top Compact Classification Strip */}
            <div className="inv-compact-top-strip">
              <div className="inv-strip-left">
                <span className="inv-section-num">01</span>
                <span className="inv-strip-heading">Diagnostic Modality</span>
              </div>
              <div className="inv-modality-pill-group">
                <button
                  type="button"
                  className={`inv-modality-btn pathology ${formDiscipline === DISCIPLINE.PATHOLOGY ? 'active' : ''}`}
                  onClick={() => handleDisciplineChange(DISCIPLINE.PATHOLOGY)}
                >
                  <span className="modality-icon">🧪</span>
                  <span className="modality-title">Pathology (Laboratory)</span>
                  {formDiscipline === DISCIPLINE.PATHOLOGY && <span className="modality-dot">●</span>}
                </button>
                <button
                  type="button"
                  className={`inv-modality-btn radiology ${formDiscipline === DISCIPLINE.RADIOLOGY ? 'active' : ''}`}
                  onClick={() => handleDisciplineChange(DISCIPLINE.RADIOLOGY)}
                >
                  <span className="modality-icon">☢️</span>
                  <span className="modality-title">Radiology (Imaging)</span>
                  {formDiscipline === DISCIPLINE.RADIOLOGY && <span className="modality-dot">●</span>}
                </button>
              </div>
            </div>

            {/* 2-Column Main Workspace */}
            <div className="inv-form-two-column">
              {/* Column 1: Test Identity & Clinical Classification */}
              <div className="inv-panel-card">
                <div className="inv-panel-header">
                  <span className="inv-panel-icon">🔬</span>
                  <h3 className="inv-panel-title">Test Identification & Classification</h3>
                </div>
                <div className="inv-panel-body">
                  <div className="inv-row-2col">
                    <div className="form-group">
                      <label className="form-label">
                        Test Code <span className="form-req">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-input ${errors.testCode ? 'border-danger' : ''}`}
                        style={{ textTransform: 'uppercase', fontFamily: 'monospace', fontWeight: 600 }}
                        value={formData.testCode}
                        onChange={(e) => handleChange('testCode', e.target.value.toUpperCase())}
                        placeholder="e.g. CBC01, LFT02"
                        required
                      />
                      {errors.testCode && <span className="error-message">{errors.testCode}</span>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        Short / Print Alias
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        value={formData.shortName}
                        onChange={(e) => handleChange('shortName', e.target.value)}
                        placeholder="e.g. CBC, LFT"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      Test / Study Full Name <span className="form-req">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-input ${errors.name ? 'border-danger' : ''}`}
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      placeholder={formDiscipline === DISCIPLINE.RADIOLOGY ? "e.g. MRI Brain with Contrast, Chest X-Ray PA" : "e.g. Complete Blood Count (CBC) with ESR"}
                      required
                    />
                    {errors.name && <span className="error-message">{errors.name}</span>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      Clinical Department / Modality <span className="form-req">*</span>
                    </label>
                    <select
                      className={`form-select ${errors.subCategoryId ? 'border-danger' : ''}`}
                      value={formData.subCategoryId}
                      onChange={(e) => handleChange('subCategoryId', e.target.value)}
                      required
                    >
                      <option value="">-- Select {formDiscipline === DISCIPLINE.RADIOLOGY ? 'Modality' : 'Department'} --</option>
                      {availableFormDepartments.map(d => (
                        <option key={d.subCategoryId} value={d.subCategoryId}>
                          {d.name}
                        </option>
                      ))}
                    </select>
                    {errors.subCategoryId && <span className="error-message">{errors.subCategoryId}</span>}
                  </div>

                  <div className="inv-row-2col">
                    <div className="form-group">
                      <label className="form-label">Report Format</label>
                      <select
                        className="form-select"
                        value={formData.reportType}
                        onChange={(e) => handleChange('reportType', Number(e.target.value))}
                      >
                        {(formDiscipline === DISCIPLINE.RADIOLOGY ? RADIOLOGY_REPORT_TYPES : PATHOLOGY_REPORT_TYPES).map(r => (
                          <option key={r.value} value={r.value}>{r.label}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Gender Applicability</label>
                      <select
                        className="form-select"
                        value={formData.genderInvestigate}
                        onChange={(e) => handleChange('genderInvestigate', e.target.value)}
                      >
                        {GENDER_OPTIONS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Column 2: Specimen & Logistics Management */}
              <div className="inv-panel-card">
                <div className="inv-panel-header">
                  <span className="inv-panel-icon">{formDiscipline === DISCIPLINE.RADIOLOGY ? '📅' : '🩸'}</span>
                  <h3 className="inv-panel-title">
                    {formDiscipline === DISCIPLINE.RADIOLOGY ? 'Study Logistics & Scheduling' : 'Specimen & Tube Logistics'}
                  </h3>
                </div>
                <div className="inv-panel-body">
                  {formDiscipline === DISCIPLINE.RADIOLOGY && (
                    <div className="discipline-notice-box radiology" style={{ margin: '0 0 12px 0', padding: '8px 12px', fontSize: '12px' }}>
                      <span className="discipline-notice-icon" style={{ fontSize: '16px' }}>ℹ️</span>
                      <div>Physical sample matrix & vacuum tube not required for diagnostic radiology.</div>
                    </div>
                  )}

                  <div className="inv-row-2col">
                    <div className="form-group">
                      <label className="form-label">Sample Matrix Type <span className="inv-hint">(one or more; one is the default)</span></label>
                      <SampleTypeMultiSelect
                        options={sampleTypes}
                        value={{ ids: formData.sampleTypeIds || [], defaultId: formData.sampleTypeId }}
                        onChange={({ ids, defaultId }) => setFormData((prev) => ({ ...prev, sampleTypeIds: ids, sampleTypeId: defaultId }))}
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Collection Tube / Container</label>
                      <select
                        className="form-select"
                        value={formData.containerId || ''}
                        onChange={(e) => handleChange('containerId', e.target.value ? Number(e.target.value) : null)}
                      >
                        <option value="">-- None / General --</option>
                        {containers.map(c => (
                          <option key={c.containerId} value={c.containerId}>
                            {c.containerName}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="inv-row-2col">
                    <div className="form-group">
                      <label className="form-label">Required Sample Volume</label>
                      <input
                        type="text"
                        className="form-input"
                        value={formData.sampleQty}
                        onChange={(e) => handleChange('sampleQty', e.target.value)}
                        placeholder="e.g. 2 ml, 5 ml"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Storage Temperature</label>
                      <select
                        className="form-select"
                        value={formData.logisticTemp}
                        onChange={(e) => handleChange('logisticTemp', e.target.value)}
                      >
                        <option value="Room Temp">Ambient (18-22°C)</option>
                        <option value="Dry Ice">Refrigerated (2-8°C)</option>
                        <option value="Frozen">Frozen (-20°C)</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Reporting TAT Intimation Limit</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.timeLimit}
                      onChange={(e) => handleChange('timeLimit', e.target.value)}
                      placeholder="e.g. 24 Hours, 48 Hours"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Strip: Compliance Flags & Action Bar */}
            <div className="inv-bottom-strip-card">
              <div className="inv-flags-grid">
                <label className="inv-flag-chip">
                  <input
                    type="checkbox"
                    checked={formData.isNabl}
                    onChange={(e) => handleChange('isNabl', e.target.checked)}
                  />
                  <span><strong>NABL Accredited</strong> (ISO 15189)</span>
                </label>

                <label className="inv-flag-chip">
                  <input
                    type="checkbox"
                    checked={formData.booking}
                    onChange={(e) => handleChange('booking', e.target.checked)}
                  />
                  <span><strong>Counter Booking</strong> (Registration)</span>
                </label>

                <label className="inv-flag-chip">
                  <input
                    type="checkbox"
                    checked={formData.printSeparate}
                    onChange={(e) => handleChange('printSeparate', e.target.checked)}
                  />
                  <span><strong>Separate Page</strong> (Dedicated)</span>
                </label>

                <label className="inv-flag-chip">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => handleChange('isActive', e.target.checked)}
                  />
                  <span><strong>Active Status</strong> (Directory)</span>
                </label>
              </div>

              <div className="inv-actions-group">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setViewMode('list')}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={saving}
                  style={{ minWidth: '130px' }}
                >
                  {saving ? 'Saving...' : (isEditMode ? 'Update Test' : 'Save Test')}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* ─── FULL-SCREEN GRID / LIST VIEW ─── */}
      {viewMode === 'list' && (
        <div className="glass-card fade-in inv-list-card">
          {/* Unified Compact Toolbar: Modality Switcher + Search + Filters */}
          <div className="inv-list-toolbar">
            {/* Left: Modality Segments */}
            <div className="inv-toolbar-modality">
              <button
                type="button"
                className={`inv-toolbar-pill ${disciplineFilter === 'all' ? 'active' : ''}`}
                onClick={() => { setDisciplineFilter('all'); setPage(1); }}
                title="All Diagnostic Tests & Studies"
              >
                <span>🌐 All</span>
                <span className="pill-count">{totalCount}</span>
              </button>
              <button
                type="button"
                className={`inv-toolbar-pill pathology ${disciplineFilter === 'pathology' ? 'active' : ''}`}
                onClick={() => { setDisciplineFilter('pathology'); setPage(1); }}
                title="Pathology Laboratory Tests"
              >
                <span>🧪 Pathology</span>
              </button>
              <button
                type="button"
                className={`inv-toolbar-pill radiology ${disciplineFilter === 'radiology' ? 'active' : ''}`}
                onClick={() => { setDisciplineFilter('radiology'); setPage(1); }}
                title="Radiology Imaging Studies"
              >
                <span>☢️ Radiology</span>
              </button>
            </div>

            {/* Middle & Right: Search & Dropdown Filters in single line */}
            <div className="inv-toolbar-filters">
              <div className="inv-search-wrap">
                <input
                  type="text"
                  className="form-input inv-search-input"
                  placeholder="Search code or test name..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                />
                <svg
                  className="inv-search-icon"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </div>

              <select
                className="form-select inv-filter-select dept-select"
                value={deptFilter}
                onChange={(e) => { setDeptFilter(e.target.value); setPage(1); }}
              >
                <option value="">
                  {disciplineFilter === 'radiology' ? 'All Modalities' : (disciplineFilter === 'pathology' ? 'All Pathology Depts' : 'All Departments')}
                </option>
                {departments
                  .filter(d => {
                    if (disciplineFilter === 'radiology') return isRadiologyDepartment(d);
                    if (disciplineFilter === 'pathology') return !isRadiologyDepartment(d);
                    return true;
                  })
                  .map(d => (
                    <option key={d.subCategoryId} value={d.subCategoryId}>
                      {d.name} {isRadiologyDepartment(d) ? '(Rad)' : '(Path)'}
                    </option>
                  ))}
              </select>

              <select
                className="form-select inv-filter-select"
                value={nablFilter}
                onChange={(e) => { setNablFilter(e.target.value); setPage(1); }}
              >
                <option value="all">All Accreditation</option>
                <option value="nabl">NABL Only</option>
                <option value="standard">Standard Only</option>
              </select>

              <select
                className="form-select inv-filter-select"
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* Table */}
          <div className="table-responsive">
            <table className="master-table mvc-table">
              <thead>
                <tr>
                  <th style={{ width: '60px', textAlign: 'center' }}>Sr.</th>
                  <th style={{ width: '120px' }}>Test Code</th>
                  <th>Investigation Name</th>
                  <th style={{ width: '190px' }}>Department & Division</th>
                  <th style={{ width: '150px' }}>Primary Sample</th>
                  <th style={{ width: '110px', textAlign: 'right' }}>MRP (₹)</th>
                  <th style={{ width: '120px', textAlign: 'center' }}>NABL</th>
                  <th style={{ width: '95px', textAlign: 'center' }}>Status</th>
                  <th style={{ width: '180px', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9" style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                      Loading clinical investigations...
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan="9" style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                      No investigations matching your criteria.
                    </td>
                  </tr>
                ) : (
                  items.map((r, index) => {
                    const isRad = isRadiologyInvestigation(r);
                    return (
                      <tr key={r.investigationId || index}>
                        <td style={{ textAlign: 'center', fontFamily: 'monospace', color: 'var(--text-muted)' }}>
                          {(page - 1) * pageSize + index + 1}
                        </td>
                        <td style={{ fontFamily: 'monospace', fontWeight: 600, color: isRad ? '#2563eb' : 'var(--accent)' }}>
                          {r.testCode}
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{r.name}</span>
                            {r.shortName && r.shortName !== r.name && (
                              <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>({r.shortName})</span>
                            )}
                            <span className={`discipline-badge ${isRad ? 'radiology' : 'pathology'}`}>
                              {isRad ? '☢️ Radiology' : '🧪 Pathology'}
                            </span>
                          </div>
                        </td>
                        <td style={{ fontSize: '0.85rem' }}>
                          <span style={{ fontWeight: 600 }}>{r.departmentName || 'General'}</span>
                        </td>
                        <td style={{ fontSize: '0.85rem' }}>
                          {isRad ? (
                            <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>PACS Study</span>
                          ) : (
                            r.primarySampleName || '—'
                          )}
                        </td>
                      <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>
                        ₹{Number(r.rate || r.baseRate || 0).toFixed(2)}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {r.isNabl ? (
                          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#047857', backgroundColor: '#ecfdf5', padding: '2px 6px', borderRadius: '4px', border: '1px solid #a7f3d0' }}>
                            ✓ NABL
                          </span>
                        ) : (
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Standard</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`badge-status ${r.isActive ? 'active' : 'inactive'}`}>
                          {r.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            style={{
                              padding: '3px 8px',
                              fontSize: '11.5px',
                              color: 'var(--accent)',
                              borderColor: 'var(--accent-light)',
                              backgroundColor: 'var(--accent-light)'
                            }}
                            onClick={() => navigate(`/investigation-observation-mapping?invId=${r.investigationId}`)}
                            title="Parameter Mapping"
                          >
                            Mapping
                          </button>

                          <button
                            type="button"
                            className="btn btn-secondary"
                            style={{ padding: '3px 8px', fontSize: '11.5px' }}
                            onClick={() => handleEditRow(r)}
                            title="Edit Investigation"
                          >
                            Edit
                          </button>

                          <button
                            type="button"
                            className="btn btn-secondary"
                            style={{
                              padding: '3px 8px',
                              fontSize: '11.5px',
                              color: r.isActive ? '#ef4444' : '#10b981'
                            }}
                            onClick={() => setConfirmTarget(r)}
                            title={r.isActive ? 'Deactivate Test' : 'Activate Test'}
                          >
                            {r.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
              </tbody>
            </table>
          </div>

          {/* Standardized LIMS Centralized Pagination */}
          <CommonPagination
            page={page}
            pageSize={pageSize}
            totalRecords={totalCount}
            onPageChange={setPage}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setPage(1);
            }}
            loading={loading}
          />
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={!!confirmTarget}
        title={confirmTarget?.isActive ? 'Deactivate Investigation' : 'Activate Investigation'}
        message={
          confirmTarget?.isActive
            ? `Are you sure you want to deactivate "${confirmTarget?.name}"?`
            : `Are you sure you want to activate "${confirmTarget?.name}"?`
        }
        confirmLabel={confirmTarget?.isActive ? 'Deactivate' : 'Activate'}
        variant={confirmTarget?.isActive ? 'danger' : 'primary'}
        loading={statusUpdating}
        onConfirm={handleConfirmStatus}
        onCancel={() => setConfirmTarget(null)}
      />
    </MasterPageWrapper>
  );
};

export default InvestigationMasterPage;
